import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Pet, UserPet } from '../types/pets';

export function usePets(walletAddress?: string) {
  const [pets, setPets] = useState<Pet[]>([]);
  const [userPets, setUserPets] = useState<UserPet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch marketplace pets
  const fetchPets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('pets')
        .select('*')
        .order('base_price', { ascending: true });

      if (error) throw error;

      setPets(data.map(pet => ({
        id: pet.id,
        name: pet.name,
        type: pet.type,
        rarity: pet.rarity,
        basePrice: pet.base_price,
        imageUrl: pet.image_url,
        stats: pet.stats
      })));
    } catch (err) {
      console.error('Error fetching pets:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch pets');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's pets
  const fetchUserPets = async () => {
    if (!walletAddress) {
      setUserPets([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('user_pets')
        .select(`
          *,
          pets:pet_id (
            type,
            rarity,
            base_price,
            image_url,
            stats
          )
        `)
        .eq('wallet_address', walletAddress);

      if (error) throw error;

      setUserPets(data.map(userPet => ({
        id: userPet.pet_id,
        name: userPet.name,
        type: userPet.pets.type,
        rarity: userPet.pets.rarity,
        basePrice: userPet.pets.base_price,
        imageUrl: userPet.pets.image_url,
        stats: userPet.pets.stats,
        level: userPet.level,
        experience: userPet.experience,
        equippedItems: userPet.equipped_items || [],
        createdAt: userPet.created_at
      })));
    } catch (err) {
      console.error('Error fetching user pets:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch user pets');
    } finally {
      setLoading(false);
    }
  };

  // Load marketplace pets on mount
  useEffect(() => {
    fetchPets();
  }, []);

  // Load user's pets when wallet address changes
  useEffect(() => {
    fetchUserPets();
  }, [walletAddress]);

  return {
    pets,
    userPets,
    loading,
    error,
    refetch: walletAddress ? fetchUserPets : fetchPets
  };
}